import { useEffect } from "react";
import "./AboutPageComponent.css";

export default function AboutPageComponent() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".reveal"));

    const inViewport = (el) => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top <= vh * 0.9 && r.bottom >= vh * 0.1;
    };

    const revealNow = () => {
      for (const el of els) {
        if (!el.classList.contains("in") && inViewport(el)) {
          el.classList.add("in");
        }
      }
    };

    revealNow();

    let io = null;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.target instanceof HTMLElement) {
              entry.target.classList.add("in");
              io.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
      );
      els.forEach((el) => {
        if (!el.classList.contains("in")) io.observe(el);
      });
    }

    let raf = null;
    const onScrollOrResize = () => {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(() => {
        revealNow();
        raf = null;
      });
    };

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    const timer = window.setTimeout(revealNow, 600);

    return () => {
      if (io) io.disconnect();
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (raf) window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <main className="about-page mtc">
      {/* HERO SECTION */}
      <section className="about-hero reveal">
        <h1 className="about-title">About Myris Tech</h1>
        <p className="about-sub">
          We’re a mission-driven software company built by engineers who have
          operated in the field. Our purpose is to deliver technology that
          enables faster, more confident decisions — when they matter most.
        </p>
      </section>

      {/* COMPANY HISTORY */}
      <hr className="mtc-hairline" />
      <section className="about-section reveal" aria-labelledby="sect-history">
        <h2 id="sect-history" className="about-heading">Our Story</h2>
        <p>
          Myris Tech was founded by engineers with experience in both private
          and public-sector operations. After witnessing how cumbersome legacy
          systems slowed missions down, we set out to bridge the gap between
          modern software engineering and real-world operational demands.
        </p>
        <p>
          From small prototypes to large-scale deployments, we’ve built
          production systems that are secure, observable, and dependable —
          not just demonstrations, but tools people trust in the field.
        </p>
      </section>

      {/* COMPANY VALUES */}
      <hr className="mtc-hairline" />
      <section className="about-section reveal" aria-labelledby="sect-values">
        <h2 id="sect-values" className="about-heading">Our Values</h2>
        <ul className="about-list">
          <li>
            <strong>Mission First.</strong> Every design decision starts from
            the mission’s outcome, not the technology stack.
          </li>
          <li>
            <strong>Trust Through Transparency.</strong> We believe resilient
            systems are those that can be understood, measured, and improved.
          </li>
          <li>
            <strong>Security by Design.</strong> From identity to telemetry,
            every layer of our stack is built with security in mind.
          </li>
          <li>
            <strong>Small, Senior Teams.</strong> We build faster and better
            with compact groups of experienced engineers.
          </li>
          <li>
            <strong>Continuous Learning.</strong> Every engagement should make
            our people — and our partners — stronger.
          </li>
        </ul>
      </section>

      {/* ENGINEERING PRINCIPLES */}
      <hr className="mtc-hairline" />
      <section className="about-section reveal" aria-labelledby="sect-principles">
        <h2 id="sect-principles" className="about-heading">Engineering Principles</h2>
        <ul className="about-list">
          <li>
            <strong>Ship Early, Ship Often.</strong> We validate in production,
            not PowerPoint.
          </li>
          <li>
            <strong>Make It Observable.</strong> Logs, traces, and metrics are
            part of the product.
          </li>
          <li>
            <strong>Automate Relentlessly.</strong> Deployment, testing,
            configuration — repeatable and predictable.
          </li>
          <li>
            <strong>Design for the Edge.</strong> Systems should degrade
            gracefully and work offline when needed.
          </li>
          <li>
            <strong>Own the Outcome.</strong> Accountability doesn’t stop at
            deployment — we stay engaged until it works.
          </li>
        </ul>
      </section>
    </main>
  );
}
